/**
/**
 * Given a property name, returns a type guard that ensures a specific property is non-nullish
 * @param key - The key that must be non-nullish
 * @returns A type guard function that takes an object and checks if the specified property is non-nullish
 *
 * @example
 * Filtering an array to only include objects with a non-nullish 'foo' property
 * ```ts
 * const arr: {foo: number | null}[] = [{ foo: 1 }, { foo: null }, { foo: 3 }];
 * const filtered = arr.filter(nonNullishPropertyGuard('foo')); 
 * // filtered is now [{ foo: 1 }, { foo: 3 }]
 * // and typescript knows all `foo` in the entries of `filtered` are numbers
 * ```
 */
export function nonNullishPropertyGuard<K extends string>(key: K) {
  return <T extends { [P in K]?: unknown }>(obj: T): obj is T & { [P in K]: NonNullable<T[P]> } => {
    return !!obj[key];
  };
}

/**
/**
 * Given a set of property names, returns a type guard that ensures each property is non-nullish
 *
 * @param keys - The keys that must be non-nullish
 * @returns A type guard function that takes an object and checks if the specified properties are non-nullish
 *
 * @example
 * const guard = nonNullishPropertiesGuard(['foo', 'bar'] as const);
 * const obj = { foo: 1, bar: 'baz', qux: null };
 * if (guard(obj)) {
 *   // obj.foo and obj.bar are guaranteed to be non-nullish here
 * }
 */
export function nonNullishPropertiesGuard<K extends string>(
  keys: readonly K[],
): <T extends Record<K, unknown>>(obj: T) => obj is T & { [P in K]: NonNullable<T[P]> } {
  return <T extends Record<K, unknown>>(obj: T): obj is T & { [P in K]: NonNullable<T[P]> } => {
    return keys.every((key) => !!obj[key]);
  };
}
